import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@dc-shared-web/lib'

/**
 * [&>div:first-child] - used to target the title/description without targeting the button
 * [&>div:first-child>svg] - used to target the icon
 */
const alertVariants = cva(
  'tw-relative tw-w-full tw-rounded-lg tw-bg-white tw-border tw-p-4 tw-flex',
  {
    variants: {
      variant: {
        default:
          'tw-border-neutral-300 [&>div:first-child]:tw-text-neutral-950 [&>div:first-child>svg]:tw-text-neutral-950',
        info: 'tw-bg-primary-50 tw-border-primary [&>div:first-child]:tw-text-primary [&>div:first-child>svg]:tw-text-primary',
        destructive:
          'tw-bg-destructive-50 tw-border-destructive [&>div:first-child]:tw-text-destructive [&>div:first-child>svg]:tw-text-destructive',
        success:
          'tw-bg-success-50 tw-border-success [&>div:first-child]:tw-text-success [&>div:first-child>svg]:tw-text-success',
        warning:
          'tw-bg-warning-50 tw-border-warning-600 [&>div:first-child]:tw-text-neutral-800 [&>div:first-child>svg]:tw-text-warning-600',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role='alert'
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
))
Alert.displayName = 'Alert'

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn(
      'tw-font-medium tw-leading-none tw-tracking-tight',
      className
    )}
    {...props}
  />
))
AlertTitle.displayName = 'AlertTitle'

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('tw-text-sm [&_p]:tw-leading-relaxed', className)}
    {...props}
  />
))
AlertDescription.displayName = 'AlertDescription'

export { Alert, AlertTitle, AlertDescription }
